/**
 * Security utilities for handling saved credentials
 * Note: This is a basic implementation. In production, consider using:
 * - Web Crypto API for proper encryption
 * - Key derivation functions (PBKDF2, scrypt, etc.)
 * - Secure storage solutions
 */

const STORAGE_KEY = 'saved_credentials';
const REMEMBER_KEY = 'remember_me';

// Simple obfuscation - NOT secure encryption
// In production, use proper encryption with Web Crypto API
const obfuscate = (data: string): string => {
    const key = 'support-coordination-key'; // In production, derive this securely
    let result = '';
    for (let i = 0; i < data.length; i++) {
        result += String.fromCharCode(
            data.charCodeAt(i) ^ key.charCodeAt(i % key.length)
        );
    }
    return btoa(result);
};

const deobfuscate = (data: string): string => {
    try {
        const decoded = atob(data);
        const key = 'support-coordination-key';
        let result = '';
        for (let i = 0; i < decoded.length; i++) {
            result += String.fromCharCode(
                decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length)
            );
        }
        return result;
    } catch {
        return '';
    }
};

export const credentialSecurity = {
    /**
     * Save credentials securely to localStorage
     */
    saveCredentials: (credentials: { username: string; password: string }): void => {
        try {
            const encrypted = obfuscate(JSON.stringify(credentials));
            localStorage.setItem(STORAGE_KEY, encrypted);
            localStorage.setItem(REMEMBER_KEY, 'true');
        } catch (error) {
            console.error('Failed to save credentials:', error);
        }
    },

    /**
     * Retrieve saved credentials from localStorage
     */
    getSavedCredentials: (): { username: string; password: string } | null => {
        try {
            const encrypted = localStorage.getItem(STORAGE_KEY);
            const rememberMe = localStorage.getItem(REMEMBER_KEY) === 'true';

            if (!encrypted || !rememberMe) {
                return null;
            }

            const decrypted = deobfuscate(encrypted);
            return JSON.parse(decrypted);
        } catch (error) {
            console.error('Failed to retrieve credentials:', error);
            // Clear corrupted data
            credentialSecurity.clearCredentials();
            return null;
        }
    },

    /**
     * Clear saved credentials from localStorage
     */
    clearCredentials: (): void => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(REMEMBER_KEY);
    },

    /**
     * Check if credentials are saved
     */
    hasCredentials: (): boolean => {
        return !!(
            localStorage.getItem(STORAGE_KEY) &&
            localStorage.getItem(REMEMBER_KEY) === 'true'
        );
    },

    /**
     * Validate credential format
     */
    validateCredentials: (credentials: any): credentials is { username: string; password: string } => {
        return (
            credentials &&
            typeof credentials === 'object' &&
            typeof credentials.username === 'string' &&
            typeof credentials.password === 'string' &&
            credentials.username.length > 0 &&
            credentials.password.length > 0
        );
    }
};